const SFX_UPLOAD_ENDPOINT = "/api/sfx";
const SFX_SELECTION_ENDPOINT = "/api/sfx/selection";

const SFX_FILES = {
  goal_piece: "assets/sound/sfx_yutnori_goal.m4a",
  move_piece: "assets/sound/sfx_yutnori_move.m4a",
  select_piece: "assets/sound/sfx_yutnori_select.m4a",
  reward_popup: "assets/sound/sfx_yutnori_reward.m4a",
  yut_throw: "assets/sound/sfx_yutnori_throw.m4a",
  yut_normal: "assets/sound/sfx_yutnori_dogaegeol.m4a",
  yut_rare: "assets/sound/sfx_yutnori_yut.m4a",
  yut_legendary: "assets/sound/sfx_yutnori_mo.m4a",
  bag_spawn: "assets/sound/sfx_yutnori_bag_spawn.m4a",
  bag_arrive: "assets/sound/sfx_yutnori_bag_arrive.m4a",
  attack_hit: "assets/sound/sfx_yutnori_attack_hit.m4a",
  land_thud: "assets/sound/sfx_yutnori_land_thud.m4a",
  button_click: "assets/sound/sfx_yutnori_button_click.m4a",
  win_fanfare: "assets/sound/sfx_yutnori_win.m4a",
  lose_fanfare: "assets/sound/sfx_yutnori_lose.m4a",
};

const DEFAULT_VOLUME = 0.7;
const SFX_DB_NAME = "yutnori_sfx_db";
const SFX_DB_VERSION = 2;
const SFX_DB_STORE = "files";
const SFX_POOL_SIZE = 4;

const sfxState = {
  enabled: true,
  volume: DEFAULT_VOLUME,
  muted: {},
  pools: new Map(),
  sources: new Map(),
  sourceBlobs: new Map(),
  buffers: new Map(),
  bufferPromises: new Map(),
  audioCtx: null,
  masterGain: null,
  useWebAudio: false,
  sourceNames: new Map(),
  filesByKey: new Map(),
  selection: new Map(),
  ready: false,
};

function canUseWebAudio() {
  return typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
}

function ensureAudioContext() {
  if (!sfxState.useWebAudio) {
    return null;
  }
  if (sfxState.audioCtx) {
    return sfxState.audioCtx;
  }
  const Context = window.AudioContext || window.webkitAudioContext;
  if (!Context) {
    return null;
  }
  const ctx = new Context();
  sfxState.audioCtx = ctx;
  sfxState.masterGain = ctx.createGain();
  sfxState.masterGain.gain.value = sfxState.volume;
  sfxState.masterGain.connect(ctx.destination);
  return ctx;
}

function decodeAudioBuffer(ctx, arrayBuffer) {
  return new Promise((resolve, reject) => {
    let done = false;
    const finish = (fn) => (value) => {
      if (done) {
        return;
      }
      done = true;
      fn(value);
    };
    const onSuccess = finish(resolve);
    const onError = finish(reject);
    const result = ctx.decodeAudioData(arrayBuffer, onSuccess, onError);
    if (result && typeof result.then === "function") {
      result.then(onSuccess).catch(onError);
    }
  });
}

export function initSfx() {
  if (sfxState.ready) {
    return;
  }
  sfxState.ready = true;
  sfxState.useWebAudio = canUseWebAudio();
  const settings = loadSfxSettings();
  if (settings) {
    sfxState.enabled = settings.enabled !== false;
    sfxState.volume = clampVolume(settings.volume);
    sfxState.muted = settings.muted || {};
  }
  const selection = loadSfxSelection();
  if (selection) {
    for (const key of Object.keys(selection)) {
      if (SFX_FILES[key]) {
        sfxState.selection.set(key, selection[key]);
      }
    }
  }
  if (sfxState.useWebAudio) {
    ensureAudioContext();
  }
  for (const key of Object.keys(SFX_FILES)) {
    buildPoolForKey(key);
  }
}

export function playSfx(key) {
  if (!sfxState.ready) {
    initSfx();
  }
  if (!sfxState.enabled || sfxState.muted[key]) {
    return;
  }
  if (sfxState.useWebAudio) {
    if (playWebAudio(key)) {
      return;
    }
  }
  const pool = sfxState.pools.get(key);
  if (!pool || !pool.items || !pool.items.length) {
    return;
  }
  try {
    let audio = pool.items.find((entry) => entry && (entry.paused || entry.ended)) || null;
    if (!audio) {
      audio = pool.items[pool.cursor] || pool.items[0];
      pool.cursor = (pool.cursor + 1) % pool.items.length;
    }
    if (!audio) {
      return;
    }
    audio.volume = sfxState.volume;
    audio.currentTime = 0;
    const result = audio.play();
    if (result && typeof result.catch === "function") {
      result.catch(() => {});
    }
  } catch (err) {
    // Ignore play errors.
  }
}

export function setSfxEnabled(enabled) {
  sfxState.enabled = !!enabled;
  saveSfxSettings();
}

export function setSfxVolume(volume) {
  sfxState.volume = clampVolume(volume);
  if (sfxState.masterGain) {
    sfxState.masterGain.gain.value = sfxState.volume;
  }
  for (const pool of sfxState.pools.values()) {
    if (!pool || !pool.items) {
      continue;
    }
    for (const audio of pool.items) {
      if (audio) {
        audio.volume = sfxState.volume;
      }
    }
  }
  saveSfxSettings();
}

export function setSfxMuted(key, muted) {
  sfxState.muted[key] = !!muted;
  saveSfxSettings();
}

export function setSfxSource(key, file) {
  if (!SFX_FILES[key]) {
    return;
  }
  if (!file) {
    return;
  }
  const entry = registerSfxFile(key, file, file.name || "custom");
  applySfxSource(key, entry.blob, entry.name);
  setSfxSelection(key, entry.id);
}

export function getSfxEntryInfo(id) {
  const entry = findCustomEntryById(id);
  if (!entry) {
    return null;
  }
  return {
    id: entry.id,
    key: entry.key,
    name: entry.name || "custom",
    url: entry.url,
    hasBlob: !!entry.blob,
  };
}

export async function deleteSfxEntry(id) {
  const entry = findCustomEntryById(id);
  if (!entry) {
    return false;
  }
  let ok = true;
  if (entry.url) {
    ok = await deleteServerSfxEntry(id);
  }
  if (entry.blob) {
    await deleteLocalSfxEntry(id);
  }
  removeSfxEntryFromCache(entry);
  return ok;
}

export async function uploadSfxFile(key, file) {
  if (!SFX_FILES[key] || !file) {
    return false;
  }
  try {
    const url = `${SFX_UPLOAD_ENDPOINT}?key=${encodeURIComponent(key)}&name=${encodeURIComponent(
      file.name || "custom"
    )}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });
    if (!response.ok) {
      return false;
    }
    const payload = await response.json();
    if (!payload || !payload.entry) {
      return false;
    }
    const entry = payload.entry;
    cacheSfxEntry(entry);
    applySelectionForKey(key, entry.id, true);
    return true;
  } catch (err) {
    return false;
  }
}

export function clearSfxSource(key, skipBuild = false) {
  const previous = sfxState.sources.get(key);
  if (previous && previous.startsWith("blob:")) {
    URL.revokeObjectURL(previous);
  }
  sfxState.sources.delete(key);
  sfxState.sourceNames.delete(key);
  sfxState.sourceBlobs.delete(key);
  sfxState.buffers.delete(key);
  sfxState.bufferPromises.delete(key);
  if (!skipBuild && SFX_FILES[key]) {
    buildPoolForKey(key);
  }
}

export function getSfxState() {
  return {
    enabled: sfxState.enabled,
    volume: sfxState.volume,
    muted: { ...sfxState.muted },
    keys: Object.keys(SFX_FILES),
  };
}

export function getSfxDisplayName(key) {
  if (!SFX_FILES[key]) {
    return "";
  }
  const customName = sfxState.sourceNames.get(key);
  if (customName) {
    return customName;
  }
  const path = SFX_FILES[key];
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

export function getSfxOptions(key) {
  if (!SFX_FILES[key]) {
    return [];
  }
  const options = [];
  const defaults = getDefaultEntries();
  for (const entry of defaults) {
    options.push({ id: entry.id, name: entry.name, isDefault: true });
  }
  const customs = getAllCustomEntries();
  for (const entry of customs) {
    options.push({ id: entry.id, name: entry.name || "custom", isDefault: false });
  }
  return options;
}

export function getSfxSelection(key) {
  if (!SFX_FILES[key]) {
    return "default";
  }
  const selection = sfxState.selection.get(key);
  if (!selection || selection === "default") {
    return getDefaultSelectionId(key);
  }
  return selection;
}

export function setSfxSelection(key, selectionId) {
  if (!SFX_FILES[key]) {
    return;
  }
  applySelectionForKey(key, selectionId, true);
}

export async function restoreSfxSources() {
  if (!sfxState.ready) {
    initSfx();
  }
  const serverSelection = await loadServerSfxSelection();
  if (serverSelection) {
    sfxState.selection = new Map();
    for (const key of Object.keys(serverSelection)) {
      if (SFX_FILES[key]) {
        sfxState.selection.set(key, serverSelection[key]);
      }
    }
  }
  sfxState.filesByKey.clear();
  const entries = [];
  const serverEntries = await loadServerSfxFiles();
  if (serverEntries.length) {
    entries.push(...serverEntries);
  }
  const localEntries = await loadSfxFiles();
  if (localEntries.length) {
    entries.push(...localEntries);
  }
  for (const entry of entries) {
    if (!entry || !entry.key || !SFX_FILES[entry.key]) {
      continue;
    }
    cacheSfxEntry(entry);
  }
  let applied = false;
  for (const key of Object.keys(SFX_FILES)) {
    const selectionId = sfxState.selection.get(key);
    if (selectionId) {
      const didApply = applySelectionForKey(key, selectionId, false);
      if (didApply) {
        applied = true;
      } else {
        applySelectionForKey(key, getDefaultSelectionId(key), true);
      }
    }
  }
  return applied;
}

export function unlockSfx() {
  if (!sfxState.ready) {
    initSfx();
  }
  const ctx = ensureAudioContext();
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
}

function saveSfxSettings() {
  // No-op: client persistence disabled.
}

function loadSfxSettings() {
  return null;
}

function saveSfxSelection() {
  const payload = {};
  for (const [key, value] of sfxState.selection.entries()) {
    payload[key] = value;
  }
  saveSfxSelectionToServer(payload);
}

function loadSfxSelection() {
  return null;
}

async function loadServerSfxSelection() {
  try {
    const response = await fetch(SFX_SELECTION_ENDPOINT, { cache: "no-store" });
    if (!response.ok) {
      return null;
    }
    const payload = await response.json();
    if (payload && typeof payload.selection === "object") {
      return payload.selection;
    }
  } catch (err) {
    // Ignore fetch errors.
  }
  return null;
}

function saveSfxSelectionToServer(selection) {
  try {
    fetch(SFX_SELECTION_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ selection: selection || {} }),
    }).catch(() => {});
  } catch (err) {
    // Ignore fetch errors.
  }
}

function registerSfxFile(key, blob, name) {
  const id = `${key}-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
  const entry = { id, key, name: name || "custom", blob, updatedAt: Date.now() };
  cacheSfxEntry(entry);
  saveSfxFile(entry);
  return entry;
}

function cacheSfxEntry(entry) {
  if (!entry || !entry.id || !entry.key) {
    return;
  }
  const list = sfxState.filesByKey.get(entry.key) || [];
  if (list.some((item) => item.id === entry.id)) {
    return;
  }
  list.push(entry);
  sfxState.filesByKey.set(entry.key, list);
}

function removeSfxEntryFromCache(entry) {
  if (!entry || !entry.id || !entry.key) {
    return;
  }
  const list = sfxState.filesByKey.get(entry.key) || [];
  const filtered = list.filter((item) => item.id !== entry.id);
  if (filtered.length) {
    sfxState.filesByKey.set(entry.key, filtered);
  } else {
    sfxState.filesByKey.delete(entry.key);
  }
}

function findSfxEntry(key, id) {
  const list = sfxState.filesByKey.get(key) || [];
  return list.find((entry) => entry.id === id) || null;
}

function getDefaultEntries() {
  return Object.keys(SFX_FILES).map((key) => ({
    id: getDefaultSelectionId(key),
    name: getSfxDisplayName(key),
    url: SFX_FILES[key],
    isDefault: true,
  }));
}

function getAllCustomEntries() {
  const entries = [];
  for (const list of sfxState.filesByKey.values()) {
    entries.push(...list);
  }
  return entries;
}

function findCustomEntryById(id) {
  for (const list of sfxState.filesByKey.values()) {
    const entry = list.find((item) => item.id === id);
    if (entry) {
      return entry;
    }
  }
  return null;
}

function getDefaultSelectionId(key) {
  return `default:${key}`;
}

function applySelectionForKey(key, selectionId, persist) {
  if (!selectionId || selectionId === "default") {
    selectionId = getDefaultSelectionId(key);
  }
  if (selectionId.startsWith("default:")) {
    const defaultKey = selectionId.slice("default:".length);
    if (!SFX_FILES[defaultKey]) {
      return false;
    }
    clearSfxSource(key, true);
    buildPoolForKey(key, SFX_FILES[defaultKey]);
    sfxState.selection.set(key, selectionId);
    if (persist) {
      saveSfxSelection();
    }
    return true;
  }
  const entry = findCustomEntryById(selectionId);
  if (!entry) {
    return false;
  }
  if (entry.url) {
    applySfxUrl(key, entry.url, entry.name);
  } else if (entry.blob) {
    applySfxSource(key, entry.blob, entry.name);
  } else {
    return false;
  }
  sfxState.selection.set(key, selectionId);
  if (persist) {
    saveSfxSelection();
  }
  return true;
}

function applySfxSource(key, blob, name) {
  clearSfxSource(key, true);
  const url = URL.createObjectURL(blob);
  sfxState.sources.set(key, url);
  sfxState.sourceBlobs.set(key, blob);
  sfxState.sourceNames.set(key, name || "custom");
  buildPoolForKey(key, url);
}

function applySfxUrl(key, url, name) {
  clearSfxSource(key, true);
  sfxState.sources.set(key, url);
  sfxState.sourceBlobs.delete(key);
  sfxState.sourceNames.set(key, name || "custom");
  buildPoolForKey(key, url);
}

function saveSfxFile(entry) {
  openSfxDb()
    .then((db) => {
      const tx = db.transaction(SFX_DB_STORE, "readwrite");
      const store = tx.objectStore(SFX_DB_STORE);
      store.put(entry);
      tx.oncomplete = () => db.close();
      tx.onerror = () => db.close();
    })
    .catch(() => {});
}

function loadSfxFiles() {
  return openSfxDb()
    .then(
      (db) =>
        new Promise((resolve) => {
          const tx = db.transaction(SFX_DB_STORE, "readonly");
          const store = tx.objectStore(SFX_DB_STORE);
          const finalize = (items) => {
            resolve(items || []);
            db.close();
          };
          if (store.getAll) {
            const request = store.getAll();
            request.onsuccess = () => finalize(request.result);
            request.onerror = () => finalize([]);
          } else {
            const items = [];
            const cursor = store.openCursor();
            cursor.onsuccess = (event) => {
              const entry = event.target.result;
              if (entry) {
                items.push(entry.value);
                entry.continue();
              } else {
                finalize(items);
              }
            };
            cursor.onerror = () => finalize([]);
          }
        })
    )
    .catch(() => []);
}

async function loadServerSfxFiles() {
  try {
    const response = await fetch(SFX_UPLOAD_ENDPOINT);
    if (!response.ok) {
      return [];
    }
    const payload = await response.json();
    if (payload && Array.isArray(payload.files)) {
      return payload.files;
    }
  } catch (err) {
    // Ignore fetch errors.
  }
  return [];
}

async function deleteServerSfxEntry(id) {
  try {
    const response = await fetch(`${SFX_UPLOAD_ENDPOINT}?id=${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      return false;
    }
    const payload = await response.json().catch(() => ({}));
    return payload && payload.ok;
  } catch (err) {
    return false;
  }
}

async function deleteLocalSfxEntry(id) {
  try {
    const db = await openSfxDb();
    await new Promise((resolve) => {
      const tx = db.transaction(SFX_DB_STORE, "readwrite");
      const store = tx.objectStore(SFX_DB_STORE);
      store.delete(id);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        resolve();
      };
    });
  } catch (err) {
    // Ignore delete errors.
  }
}

function openSfxDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const request = indexedDB.open(SFX_DB_NAME, SFX_DB_VERSION);
    request.onerror = () => reject(request.error || new Error("IndexedDB error"));
    request.onupgradeneeded = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(SFX_DB_STORE)) {
        db.deleteObjectStore(SFX_DB_STORE);
      }
      db.createObjectStore(SFX_DB_STORE, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
  });
}

function buildPoolForKey(key, overrideUrl) {
  const url = overrideUrl || sfxState.sources.get(key) || SFX_FILES[key];
  if (!url) {
    return;
  }
  if (sfxState.useWebAudio) {
    sfxState.pools.delete(key);
    ensureWebAudioBuffer(key, url);
    return;
  }
  buildHtmlAudioPool(key, url);
}

function buildHtmlAudioPool(key, url) {
  const items = [];
  for (let i = 0; i < SFX_POOL_SIZE; i += 1) {
    const audio = new Audio(url);
    audio.preload = "auto";
    audio.volume = sfxState.volume;
    audio.load();
    items.push(audio);
  }
  sfxState.pools.set(key, { items, cursor: 0 });
}

function ensureWebAudioBuffer(key, url) {
  if (sfxState.buffers.has(key) || sfxState.bufferPromises.has(key)) {
    return;
  }
  const blob = sfxState.sourceBlobs.get(key) || null;
  const task = loadWebAudioBuffer(key, url, blob);
  sfxState.bufferPromises.set(key, task);
}

async function loadWebAudioBuffer(key, url, blob) {
  try {
    const ctx = ensureAudioContext();
    if (!ctx) {
      return null;
    }
    const arrayBuffer = blob
      ? await blob.arrayBuffer()
      : await fetch(url, { cache: "force-cache" }).then((res) => res.arrayBuffer());
    const buffer = await decodeAudioBuffer(ctx, arrayBuffer);
    sfxState.buffers.set(key, buffer);
    return buffer;
  } catch (err) {
    // Fallback to HTMLAudio for this key if decode fails.
    buildHtmlAudioPool(key, url);
    return null;
  } finally {
    sfxState.bufferPromises.delete(key);
  }
}

function playWebAudio(key) {
  const ctx = ensureAudioContext();
  if (!ctx) {
    return false;
  }
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
    if (ctx.state === "suspended") {
      return false;
    }
  }
  const buffer = sfxState.buffers.get(key);
  if (!buffer) {
    if (!sfxState.bufferPromises.has(key)) {
      buildPoolForKey(key);
    }
    return false;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const destination = sfxState.masterGain || ctx.destination;
  source.connect(destination);
  try {
    source.start(0);
  } catch (err) {
    return false;
  }
  return true;
}

function clampVolume(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return DEFAULT_VOLUME;
  }
  return Math.max(0, Math.min(1, num));
}
